/*
Copyright (C) 2020 Broadleaf Commerce.

Licensed under the Broadleaf End User License Agreement (EULA),
Version 1.1 (the “Commercial License” located at
http://license.broadleafcommerce.org/commercial_license-1.1.txt).

Alternatively, the Commercial License may be replaced with a mutually
agreed upon license (the “Custom License”) between you and
Broadleaf Commerce. You may not use this file except in compliance
with the applicable license.
*/
import React, { Dispatch, FC } from 'react';
import { map } from 'lodash';

import type { ItemsOrderViewSectionComponents } from '..';
import {
  IActionComponent,
  IMetadata
} from '@broadleaf/admin-components/dist/types/metadata';
import { IOrderLocalState } from '@broadleaf/admin-components/dist/types/oms';
import { useVisibleActionDefinitions } from '@broadleaf/admin-components/dist/metadata';
import { useOrderFulfillments } from '@broadleaf/admin-components/dist/oms/components';
import {
  selectCurrentItems,
  selectCurrentPage
} from '@broadleaf/admin-components/dist/collection/hooks/useCollectionState';
import { Spinner } from '@broadleaf/admin-components/dist/common';
import betweenWith from '@broadleaf/admin-components/dist/common/utils/lodash/betweenWith';
import { SectionPagination } from '@broadleaf/admin-components/dist/oms/elements';

export interface FulfillmentCardsProps {
  actionComponents: Array<IActionComponent>;
  itemsViewComponents: ItemsOrderViewSectionComponents;
  metadata: IMetadata;
  orderDispatch: Dispatch<any>;
  orderMetadata: IMetadata;
  orderState: IOrderLocalState;
}

export const FulfillmentCards: FC<FulfillmentCardsProps> = ({
  actionComponents,
  itemsViewComponents,
  metadata,
  orderMetadata,
  orderDispatch,
  orderState
}) => {
  const actionDefinitions = useVisibleActionDefinitions(metadata);
  const { collectionState: fulfillmentsState } = useOrderFulfillments({
    metadata,
    orderMetadata,
    orderState
  });
  const currentPage = selectCurrentPage(fulfillmentsState.state);
  const fulfillments = selectCurrentItems(fulfillmentsState.state);
  return (
    <div className="FulfillmentCards tw-pb-5">
      {currentPage.result?.length === 0 && currentPage.isFetching && (
        <div className="tw-w-full tw-text-center">
          <Spinner
            className="tw-mb-5 tw-border-gray-300"
            color="info"
            size="md"
          />
        </div>
      )}
      {betweenWith(
        map(fulfillments, fulfillment => {
          return (
            <itemsViewComponents.FulfillmentCard
              key={fulfillment.id}
              actionComponents={actionComponents}
              actionDefinitions={actionDefinitions}
              fulfillment={fulfillment}
              fulfillmentsState={fulfillmentsState}
              itemsMetadata={metadata}
              itemsViewComponents={itemsViewComponents}
              orderDispatch={orderDispatch}
              orderMetadata={orderMetadata}
              orderState={orderState}
            />
          );
        }),
        (_, __, index) => (
          <div key={`spacer-${index}`} className="tw-py-4" />
        )
      )}
      <SectionPagination
        currentPage={currentPage}
        onPageChange={fulfillmentsState.handlePageChange}
      />
    </div>
  );
};

export default FulfillmentCards;
